import { createFileRoute, useSearch } from "@tanstack/react-router";
import { dehydrate, DehydratedState, hydrate, useQuery, useQueryClient } from "@tanstack/react-query";
import { businessesQueryOptions, businessQueryOptions } from "@/server/business-directory";
import { BusinessForm } from "../-components/business-form";
import { BusinessReadable } from "@/lib/mma";
import { useEffect } from "react";

export const Route = createFileRoute("/_dashboard/business-directory/businesses/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    const id = search.id as number | string | undefined;
    const parentId = search.parentId as number | undefined;
    return { id, parentId };
  },
  loaderDeps: ({ search }) => {
    return { id: search.id, parentId: search.parentId };
  },
  loader: async ({ context, deps }) => {
    const { id } = deps;

    if (id && id !== "new") {
      try {
        const queryOptions = businessQueryOptions(id as number);
        await context.queryClient.prefetchQuery(queryOptions);
        const businessData = context.queryClient.getQueryData(queryOptions.queryKey) as BusinessReadable;
        const businessName = businessData?.name || `Business ${id}`;
        return {
          crumb: businessName,
          dehydratedState: dehydrate(context.queryClient) as any,
        };
      } catch (error) {
        return {
          crumb: `Business ${id}`,
          dehydratedState: dehydrate(context.queryClient) as any,
        };
      }
    } else if (id === "new") {
      return {
        crumb: "New Business",
        dehydratedState: dehydrate(context.queryClient) as any,
      };
    }
    return {
      crumb: "Businesses",
    };
  },
});

function RouteComponent() {
  const { dehydratedState } = Route.useLoaderData() as { dehydratedState: DehydratedState };
  const queryClient = useQueryClient();
  hydrate(queryClient, dehydratedState);
  const search = useSearch({ from: "/_dashboard/business-directory/businesses/" });

  const isNew = search.id === "new";
  const parentId = search.parentId;

  const businessesQuery = useQuery(businessesQueryOptions);
  const businesses = businessesQuery.data;
  const businessQuery = useQuery(businessQueryOptions(typeof search.id === "number" ? search.id : 0));
  const business = businessQuery.data;

  useEffect(() => {
    if (!search.id) return;
    void businessQuery.refetch();
  }, [search.id, businessQuery.refetch]);

  return (
    <BusinessForm
      business={business as BusinessReadable}
      businesses={businesses}
      isEdit={!isNew}
      parentId={parentId ? parentId : null}
    />
  );
}
